/* eslint-disable max-len */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { v4 as uuidv4 } from 'uuid';
import { NO_FILE } from 'components/FileUpload/paramsByState';
import { getUploadedReferencesFromFile, setNewUploadedReference } from 'domains/promotion/Promotion.store';
import {
  CreatePromotionModel,
  ReferencesSectionLocal,
  UploadedReferencesFromFile,
} from 'domains/promotion/Promotion.types';
import AddPromotedReferences from 'pages/BackOfficePage/BackOfficeCategories/Promotion/CreatePromotion/PriceOptionStep/AddPromotedReferences';
import { Flex, MarginBox, PlusCircleButton, Text } from 'UI';
import { AddPromotedReference } from './AddPromotedReferece';
import { AlreadyPromotedReferencesNotification } from './PromotionErrorNotifications';
import { UploadedReferences } from './UploadedReferences';

export function shouldDisplayAddCsvButton(
  uploadedReferences: UploadedReferencesFromFile[],
  maxFileCount: number,
): boolean {
  const lastRef = uploadedReferences.at(-1);
  return uploadedReferences.length > 0 && lastRef?.status !== NO_FILE && uploadedReferences.length < maxFileCount;
}

interface ByReferencesProps {
  promotion: CreatePromotionModel;
  setPromotion: (x: CreatePromotionModel) => void;
  setCurrentStep: (x: number) => void;
  maxFileCount: number;
  maxReferencesGroups: number;
}

export function ReferencesDiscountRow({
  promotion,
  setPromotion,
  setCurrentStep,
  maxFileCount,
  maxReferencesGroups,
}: Readonly<ByReferencesProps>) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const uploadedReferences = useSelector(getUploadedReferencesFromFile);
  const [displayAddCsvButton, setDisplayAddCsvButton] = useState<boolean>(false);
  const [displayAddReferenceButton, setDisplayAddReferenceButton] = useState<boolean>(false);

  const referencesSections = useMemo(() => promotion.references ?? [{ items: [], discount: 0 }], [promotion]);

  const handleDisplayAddCsvButton = useCallback(() => {
    setDisplayAddCsvButton(shouldDisplayAddCsvButton(uploadedReferences, maxFileCount));
  }, [uploadedReferences, maxFileCount]);

  const handleDisplayAddReferenceButton = useCallback(() => {
    setDisplayAddReferenceButton(
      referencesSections.filter(
        (section: ReferencesSectionLocal) => section.items.length === 0 && section.discount === 0,
      ).length === 0 && referencesSections.length < maxReferencesGroups,
    );
  }, [referencesSections, maxReferencesGroups]);

  useEffect(() => {
    handleDisplayAddCsvButton();
  }, [handleDisplayAddCsvButton]);

  useEffect(() => {
    handleDisplayAddReferenceButton();
  }, [handleDisplayAddReferenceButton]);

  const addFile = () => {
    dispatch(setNewUploadedReference({ fileId: uuidv4() }));
    setDisplayAddCsvButton(false);
  };

  const addReferenceLine = () => {
    if (promotion.references && promotion.references.length >= maxReferencesGroups) {
      return;
    }
    setDisplayAddReferenceButton(false);
    setPromotion({
      ...promotion,
      references: [...(promotion.references ?? []), { items: [], discount: 0 }],
    });
  };

  return (
    <Flex direction={'column'} gap={15} data-cy={'by-references-section'}>
      {maxFileCount > 0 ? (
        <>
          <Flex>
            <Text type={'h5_bold'}>{t('backoffice.promotion.by_references', 'By references')}</Text>
          </Flex>
          <Flex direction={'column'}>
            <AlreadyPromotedReferencesNotification promotion={promotion} setCurrentStep={setCurrentStep} />
            <AddPromotedReferences promotion={promotion} setPromotion={setPromotion} setCurrentStep={setCurrentStep} />
            {displayAddReferenceButton && (
              <>
                <MarginBox mt={30} />
                <PlusCircleButton onClick={addReferenceLine}>
                  <Text type="base" bold color="clear_blue" decoration={'underline'}>
                    {t('backoffice.promotion.add_reference', 'Add reference')}
                  </Text>
                </PlusCircleButton>
              </>
            )}
          </Flex>
          {[...uploadedReferences.values()].map((uploadedReference, index) => (
            <UploadedReferences
              key={`ReferencesDiscountRow_${index}`}
              uploadedReferences={uploadedReference}
              promotion={promotion}
              setPromotion={setPromotion}
              setCurrentStep={setCurrentStep}
              index={index}
            />
          ))}
          {displayAddCsvButton && (
            <>
              <PlusCircleButton onClick={addFile} data-cy={'add-csv-file-button'}>
                <Text type="base" bold color="clear_blue" decoration={'underline'}>
                  {t('backoffice.promotion.add_csv_file', 'Add CSV file')}
                </Text>
              </PlusCircleButton>
              <MarginBox mt={15} />
            </>
          )}
        </>
      ) : (
        <AddPromotedReference promotion={promotion} setPromotion={setPromotion} setCurrentStep={setCurrentStep} />
      )}
    </Flex>
  );
}
